import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { FilterInDropdown } from '@sorare/core/src/atoms/dropdowns/FilterInDropdown';
import { CheckboxGroup } from '@sorare/core/src/atoms/inputs/CheckboxGroup';
import { LabelM } from '@sorare/core/src/atoms/typography';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { usePaginatedQuery } from '@sorare/core/src/hooks/graphql/usePaginatedQuery';
import { useMarkNotificationsAsRead } from '@sorare/core/src/hooks/notification/useMarkNotificationsAsRead';
import { useInfiniteScroll } from '@sorare/core/src/hooks/useInfiniteScroll';
import type { NotificationGroup } from '@sorare/core/src/lib/notifications';
import {
  allGroupsButAnnouncements,
  getNotificationCategories,
  messages,
} from '@sorare/core/src/lib/notifications';
import { getFormattedSport } from '@sorare/core/src/lib/sports';

import { Notification } from '#src/components/activity/Notification/index.tsx';
import {
  FiltersContainer,
  ItemsContainer,
  Root,
} from '#src/components/activity/ui.tsx';

import type {
  GetNotificationsQuery,
  GetNotificationsQueryVariables,
} from './__generated__/index.graphql.ts';

const GET_NOTIFICATIONS_QUERY = gql`
  query GetNotificationsQuery(
    $cursor: String
    $categories: [NotificationCategoryInput!]
    $sports: [Sport!]
  ) {
    currentUser {
      slug
      notifications: anyNotifications(
        after: $cursor
        categories: $categories
        sports: $sports
      ) {
        nodes {
          id
          ...Notification_notification
        }
        pageInfo {
          endCursor
          hasNextPage
        }
      }
    }
  }
  ${Notification.fragments.notification}
` as TypedDocumentNode<GetNotificationsQuery, GetNotificationsQueryVariables>;

const sports = Object.values(Sport);

export const Notifications = () => {
  const markNotificationsAsRead = useMarkNotificationsAsRead();
  const [selectedGroups, setSelectedGroups] = useState<NotificationGroup[]>([]);
  const { sport } = useSportContext();
  const [selectedSports, setSelectedSports] = useState<Sport[]>([sport]);
  const { data, loading, loadMore } = usePaginatedQuery(
    GET_NOTIFICATIONS_QUERY,
    {
      variables: {
        sports: selectedSports,
        categories: getNotificationCategories(
          selectedGroups.length ? selectedGroups : allGroupsButAnnouncements
        ),
      },
      connection: 'NotificationInterfaceConnection',
    }
  );

  const notifications = data?.currentUser?.notifications;

  useEffect(() => {
    const read = notifications?.nodes?.filter(Boolean);
    if (read?.length) markNotificationsAsRead(read);
  }, [notifications?.nodes, markNotificationsAsRead]);

  const hasNextPage = Boolean(
    data?.currentUser?.notifications?.pageInfo?.hasNextPage
  );

  const { InfiniteScrollLoader } = useInfiniteScroll(
    () => {
      loadMore(false, {
        cursor: data?.currentUser?.notifications?.pageInfo.endCursor,
      });
    },
    hasNextPage,
    loading
  );

  return (
    <Root>
      <FiltersContainer>
        <FilterInDropdown
          buttonLabel={
            <FormattedMessage
              defaultMessage="{nbSports, plural, =0 {All sports} other {Sports (#)}}"
              values={{
                nbSports: selectedSports.length,
              }}
            />
          }
          filterSelected={!!selectedSports.length}
          onClearFilter={() => setSelectedSports([])}
        >
          <CheckboxGroup
            options={sports.map(s => ({
              value: s,
              label: <LabelM>{getFormattedSport(s)}</LabelM>,
            }))}
            selectedValues={selectedSports}
            onChange={setSelectedSports}
          />
        </FilterInDropdown>
        <FilterInDropdown
          buttonLabel={
            <FormattedMessage
              defaultMessage="{nbCategories, plural, =0 {All categories} other {Categories (#)}}"
              values={{
                nbCategories: selectedGroups.length,
              }}
            />
          }
          filterSelected={!!selectedGroups.length}
          onClearFilter={() => setSelectedGroups([])}
        >
          <CheckboxGroup
            options={allGroupsButAnnouncements.map(group => ({
              value: group,
              label: (
                <LabelM>
                  <FormattedMessage {...messages[group]} />
                </LabelM>
              ),
            }))}
            selectedValues={selectedGroups}
            onChange={setSelectedGroups}
          />
        </FilterInDropdown>
      </FiltersContainer>
      <ItemsContainer>
        {notifications?.nodes?.map(notification => {
          if (!notification) return null;
          return (
            <Notification key={notification.id} notification={notification} />
          );
        })}
        {!!data && !notifications?.nodes?.length && (
          <LabelM>
            <FormattedMessage defaultMessage="You have no notifications yet." />
          </LabelM>
        )}
        <InfiniteScrollLoader />
      </ItemsContainer>
    </Root>
  );
};
